// src/components/messages/Inbox.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search,
  ChevronLeft,
  Download,
  Trash2,
  Star,
  Mail,
  MailOpen,
  Archive,
  AlertCircle,
  Paperclip,
  Calendar,
  User,
  RotateCcw,
  Send,
  Forward,
  FileText,
  RefreshCw,
  X
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from "../../utils/axios";
import RichTextViewer from '../common/RichTextViewer';
import RichTextEditor from '../common/RichTextEditor';
import uploadEditorImage from '../../utils/uploadEditorImage';
import { showToast } from '../../utils/toast';

// Function to parse any content format to HTML for display
const parseToHTML = (content) => {
  if (!content) return '';

  // If it's already HTML (contains tags), return as is
  if (typeof content === 'string' && /<[a-z][\s\S]*>/i.test(content)) {
    return content;
  }

  // Check if it looks like a JSON string (starts with { and contains "ops")
  if (typeof content === 'string') {
    const trimmed = content.trim();
    
    // Handle object format: {"ops": [...]}
    if (trimmed.startsWith('{') && trimmed.includes('"ops"')) {
      try {
        const parsed = JSON.parse(content);
        if (parsed.ops && Array.isArray(parsed.ops)) {
          return quillDeltaToHTML(parsed);
        }
      } catch (e) {
        console.log('Error parsing JSON object:', e);
      }
    }
    
    // Handle object format: {"message": "...", "compiledLetter": "..."}
    if (trimmed.startsWith('{') && trimmed.includes('"message"')) {
      try {
        const parsed = JSON.parse(content);
        const value = parsed.compiledLetter || parsed.message;
        if (typeof value === 'string' && value) {
          return parseToHTML(value);
        }
      } catch (e) {
        console.log('Error parsing JSON message object:', e);
      }
    }
    
    // Handle array format: [{"insert": "text"}]
    if (trimmed.startsWith('[') && trimmed.includes('"insert"')) {
      try {
        const parsed = JSON.parse(content);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].insert !== undefined) {
          return quillDeltaToHTML({ ops: parsed });
        }
      } catch (e) {
        console.log('Error parsing JSON array:', e);
      }
    }
    
    // Check if it's a plain text string without HTML
    if (!trimmed.includes('<')) {
      return trimmed.split('\n').map(line => `<p>${line}</p>`).join('');
    }
  }

  // Try to parse as JSON object (if it's already an object)
  if (typeof content === 'object' && content !== null) {
    if (content.ops && Array.isArray(content.ops)) {
      return quillDeltaToHTML(content);
    }
    // Handle array object
    if (Array.isArray(content) && content.length > 0 && content[0].insert !== undefined) {
      return quillDeltaToHTML({ ops: content });
    }
  }

  return typeof content === 'string' ? content : '';
};

// Convert Quill Delta to HTML
const quillDeltaToHTML = (delta) => {
  if (!delta || !delta.ops || !Array.isArray(delta.ops)) return '';

  let html = '';
  let listStack = [];
  let inParagraph = false;

  delta.ops.forEach(op => {
    let text = op.insert || '';
    
    // Handle newlines
    if (text === '\n') {
      // Close any open lists
      while (listStack.length > 0) {
        const listType = listStack.pop();
        html += `</${listType}>`;
      }
      if (inParagraph) {
        html += '</p>';
        inParagraph = false;
      }
      return;
    }

    // Skip empty text
    if (!text.trim() && text !== '\n') return;

    // Handle text with attributes
    if (op.attributes) {
      let formattedText = text;
      
      // Apply formatting in correct order
      if (op.attributes.bold) formattedText = `<strong>${formattedText}</strong>`;
      if (op.attributes.italic) formattedText = `<em>${formattedText}</em>`;
      if (op.attributes.underline) formattedText = `<u>${formattedText}</u>`;
      if (op.attributes.strike) formattedText = `<s>${formattedText}</s>`;
      if (op.attributes.link) formattedText = `<a href="${op.attributes.link}" target="_blank" rel="noopener noreferrer">${formattedText}</a>`;
      
      // Handle headers
      if (op.attributes.header) {
        const level = op.attributes.header;
        if (inParagraph) {
          html += '</p>';
          inParagraph = false;
        }
        // Close any open lists
        while (listStack.length > 0) {
          const listType = listStack.pop();
          html += `</${listType}>`;
        }
        html += `<h${level}>${formattedText.replace(/\n/g, '<br>')}</h${level}>`;
        return;
      }
      
      // Handle alignment
      if (op.attributes.align) {
        if (inParagraph) {
          html += '</p>';
          inParagraph = false;
        }
        // Close any open lists
        while (listStack.length > 0) {
          const listType = listStack.pop();
          html += `</${listType}>`;
        }
        html += `<div style="text-align: ${op.attributes.align}">${formattedText.replace(/\n/g, '<br>')}</div>`;
        return;
      }

      // Handle lists
      if (op.attributes.list) {
        const listType = op.attributes.list === 'bullet' ? 'ul' : 'ol';
        
        // Close any open paragraph
        if (inParagraph) {
          html += '</p>';
          inParagraph = false;
        }
        
        // Open list if not already open
        if (listStack.length === 0 || listStack[listStack.length - 1] !== listType) {
          if (listStack.length > 0) {
            html += `</${listStack.pop()}>`;
          }
          html += `<${listType}>`;
          listStack.push(listType);
        }
        
        html += `<li>${formattedText.replace(/\n/g, '<br>')}</li>`;
        return;
      }

      // Handle indent
      if (op.attributes.indent) {
        if (inParagraph) {
          html += '</p>';
          inParagraph = false;
        }
        // Close any open lists
        while (listStack.length > 0) {
          const listType = listStack.pop();
          html += `</${listType}>`;
        }
        html += `<div style="margin-left: ${op.attributes.indent * 20}px">${formattedText.replace(/\n/g, '<br>')}</div>`;
        return;
      }

      // Regular formatted text
      if (!inParagraph) {
        html += '<p>';
        inParagraph = true;
      }
      html += formattedText.replace(/\n/g, '<br>');
    } else {
      // Plain text
      if (text.trim()) {
        if (!inParagraph) {
          html += '<p>';
          inParagraph = true;
        }
        html += text.replace(/\n/g, '<br>');
      }
    }
  });

  // Close any open lists
  while (listStack.length > 0) {
    const listType = listStack.pop();
    html += `</${listType}>`;
  }

  // Close any open paragraph
  if (inParagraph) {
    html += '</p>';
  }

  return html;
};

// Function to truncate subject
const truncateSubject = (subject, maxLength = 30) => {
  if (!subject) return '';
  if (subject.length <= maxLength) return subject;
  return subject.substring(0, maxLength) + '...';
};

const Inbox = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showEmailContent, setShowEmailContent] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const [activeTab, setActiveTab] = useState('inbox'); // inbox, unread, archived, trash, sent
  const [emails, setEmails] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    inbox: 0,
    unread: 0,
    archived: 0,
    trash: 0,
    sent: 0
  });
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [forwardingEmail, setForwardingEmail] = useState(null);
  const [forwardRecipients, setForwardRecipients] = useState([]);
  const [forwardToInput, setForwardToInput] = useState('');
  const [forwardSubject, setForwardSubject] = useState('');
  const [forwardBody, setForwardBody] = useState('');
  const [forwardSelectedAttachments, setForwardSelectedAttachments] = useState([]);
  const [forwardSending, setForwardSending] = useState(false);

  // Fetch message counts
  const fetchCounts = async () => {
    try {
      const response = await axios.get('/inbox/api/counts');
      if (response.data.status === 'success') {
        setCounts(response.data.counts);
      }
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  // Fetch messages based on active tab and search
  const fetchMessages = async () => {
    setLoading(true);
    setNetworkError(false);
    
    try {
      const params = {
        folder: activeTab,
        ...(searchTerm && { search: searchTerm })
      };
      
      const response = await axios.get('/inbox/api', { params });
      
      if (response.data.status === 'success') {
        setEmails(response.data.emails);
        // Refresh counts after fetching messages
        fetchCounts();
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      setNetworkError(true);
      showToast('Failed to load messages. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load messages on mount and when dependencies change
  useEffect(() => {
    if (user?.email) {
      fetchMessages();
    }
  }, [activeTab, searchTerm, user]);

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 1) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Handle email click to view details
  const handleEmailClick = async (email) => {
    try {
      // Fetch full email details including CC, BCC, and attachments
      const response = await axios.get(`/inbox/api/${email.id}`);
      
      if (response.data.status === 'success') {
        // Parse the email body to ensure proper formatting
        const parsedEmail = {
          ...response.data.email,
          body: parseToHTML(response.data.email.body)
        };
        setSelectedEmail(parsedEmail);
        setShowEmailContent(true);
        
        // If it was unread, update local state
        if (email.status === 'unread') {
          setEmails(prev => prev.map(e => 
            e.id === email.id ? { ...e, status: 'read' } : e
          ));
          fetchCounts(); // Refresh counts
        }
      }
    } catch (error) {
      console.error('Error fetching email details:', error);
      showToast('Failed to load email details', 'error');
    }
  };

  // Handle back to list
  const handleBackToList = () => {
    setShowEmailContent(false);
    setSelectedEmail(null);
  };

  // Handle starring/unstarring email
  const handleStarEmail = async (e, email) => {
    e.stopPropagation();
    
    try {
      const response = await axios.post(`/inbox/api/${email.id}/star`);
      
      if (response.data.status === 'success') {
        // Update local state
        setEmails(prev => prev.map(e =>
          e.id === email.id ? { ...e, starred: response.data.starred } : e
        ));
        
        if (selectedEmail?.id === email.id) {
          setSelectedEmail(prev => ({ ...prev, starred: response.data.starred }));
        }
        
        showToast(response.data.message, 'success');
      }
    } catch (error) {
      console.error('Error starring email:', error);
      showToast('Failed to update star status', 'error');
    }
  };

  // Handle archive email
  const handleArchiveEmail = async (e, email) => {
    e.stopPropagation();
    
    try {
      const response = await axios.put(`/inbox/api/${email.id}/status`, {
        status: 'archived'
      });
      
      if (response.data.status === 'success') {
        // Remove from current list
        setEmails(prev => prev.filter(e => e.id !== email.id));
        
        if (selectedEmail?.id === email.id) {
          setShowEmailContent(false);
          setSelectedEmail(null);
        }
        
        fetchCounts(); // Refresh counts
        
        showToast('Email archived successfully', 'success');
      }
    } catch (error) {
      console.error('Error archiving email:', error);
      showToast('Failed to archive email', 'error');
    }
  };

  // Handle delete email (move to trash)
  const handleDeleteEmail = (e, email) => {
    e.stopPropagation();
    setEmailToDelete(email);
    setShowDeleteConfirm(true);
  };

  // Confirm delete (move to trash)
  const confirmDelete = async () => {
    if (emailToDelete) {
      try {
        const response = await axios.put(`/inbox/api/${emailToDelete.id}/status`, {
          status: 'trashed'
        });
        
        if (response.data.status === 'success') {
          setEmails(prev => prev.filter(e => e.id !== emailToDelete.id));
          
          if (selectedEmail?.id === emailToDelete.id) {
            setShowEmailContent(false);
            setSelectedEmail(null);
          }
          
          fetchCounts(); // Refresh counts
          
          showToast('Email moved to trash', 'success');
        }
      } catch (error) {
        console.error('Error moving email to trash:', error);
        showToast('Failed to move email to trash', 'error');
      }
      
      setShowDeleteConfirm(false);
      setEmailToDelete(null);
    }
  };

  // Handle restore email from trash
  const handleRestoreEmail = async (e, email) => {
    e.stopPropagation();
    
    try {
      const response = await axios.put(`/inbox/api/${email.id}/status`, {
        status: 'read'
      });
      
      if (response.data.status === 'success') {
        setEmails(prev => prev.filter(e => e.id !== email.id));
        
        if (selectedEmail?.id === email.id) {
          setShowEmailContent(false);
          setSelectedEmail(null);
        }
        
        fetchCounts(); // Refresh counts
        
        showToast('Email restored to inbox', 'success');
      }
    } catch (error) {
      console.error('Error restoring email:', error);
      showToast('Failed to restore email', 'error');
    }
  };

  // Handle permanent delete
  const handlePermanentDelete = async (e, email) => {
    e.stopPropagation();
    
    if (window.confirm('This will permanently delete this email. Are you sure?')) {
      try {
        const response = await axios.delete(`/inbox/api/${email.id}`);
        
        if (response.data.status === 'success') {
          setEmails(prev => prev.filter(e => e.id !== email.id));
          
          if (selectedEmail?.id === email.id) {
            setShowEmailContent(false);
            setSelectedEmail(null);
          }
          
          fetchCounts(); // Refresh counts
          
          showToast('Email permanently deleted', 'success');
        }
      } catch (error) {
        console.error('Error deleting email:', error);
        showToast('Failed to delete email', 'error');
      }
    }
  };

  // Handle empty trash
  const handleEmptyTrash = async () => {
    if (window.confirm('Are you sure you want to permanently delete all emails in trash?')) {
      // Get all trashed email IDs
      const trashedIds = emails.map(e => e.id);
      
      if (trashedIds.length === 0) return;
      
      try {
        const response = await axios.post('/inbox/api/bulk', {
          action: 'delete',
          messageIds: trashedIds
        });
        
        if (response.data.status === 'success') {
          setEmails([]);
          fetchCounts(); // Refresh counts
          
          showToast('Trash emptied successfully', 'success');
        }
      } catch (error) {
        console.error('Error emptying trash:', error);
        showToast('Failed to empty trash', 'error');
      }
    }
  };

  // Handle restore all from trash
  const handleRestoreAll = async () => {
    const trashedIds = emails.map(e => e.id);
    
    if (trashedIds.length === 0) return;
    
    try {
      const response = await axios.post('/inbox/api/bulk', {
        action: 'restore',
        messageIds: trashedIds
      });
      
      if (response.data.status === 'success') {
        setEmails([]);
        fetchCounts(); // Refresh counts
        
        showToast('All emails restored from trash', 'success');
      }
    } catch (error) {
      console.error('Error restoring emails:', error);
      showToast('Failed to restore emails', 'error');
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    if (selectedEmails.length === 0) return;
    
    try {
      const response = await axios.post('/inbox/api/bulk', {
        action,
        messageIds: selectedEmails
      });
      
      if (response.data.status === 'success') {
        // Refresh messages
        fetchMessages();
        setSelectedEmails([]);
        setSelectAll(false);
        
        const actionMessages = {
          archive: 'Emails archived',
          trash: 'Emails moved to trash',
          restore: 'Emails restored',
          mark_read: 'Emails marked as read',
          mark_unread: 'Emails marked as unread'
        };
        
        showToast(actionMessages[action] || 'Bulk action completed', 'success');
      }
    } catch (error) {
      console.error('Error performing bulk action:', error);
      showToast('Failed to perform bulk action', 'error');
    }
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(emails.map(e => e.id));
    }
    setSelectAll(!selectAll);
  };

  // Handle individual email selection
  const handleSelectEmail = (e, emailId) => {
    e.stopPropagation();
    
    setSelectedEmails(prev => {
      if (prev.includes(emailId)) {
        return prev.filter(id => id !== emailId);
      } else {
        return [...prev, emailId];
      }
    });
  };

  // Download attachment
  const handleDownloadAttachment = async (emailId, attachmentId, fileName) => {
    try {
      const response = await axios.get(`/inbox/api/${emailId}/attachments/${attachmentId}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading attachment:', error);
      showToast('Failed to download attachment', 'error');
    }
  };

  // Escape HTML special characters to prevent XSS in forwarded content
  const escapeHtml = (text) => {
    if (!text) return '';
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  // Open forward modal with quoted original message
  const handleForwardClick = () => {
    if (!selectedEmail) return;

    setForwardingEmail(selectedEmail);
    setForwardRecipients([]);
    setForwardToInput('');

    const dateStr = (selectedEmail.sent_at || selectedEmail.date_sent)
      ? new Date(selectedEmail.sent_at || selectedEmail.date_sent).toLocaleString()
      : '';

    const ccHtml = selectedEmail.cc?.length
      ? `<p><strong>CC:</strong> ${escapeHtml(selectedEmail.cc.join(', '))}</p>`
      : '';

    const quotedBody = `
      <p><br></p>
      <p>---------- Forwarded message ----------</p>
      <p><strong>From:</strong> ${escapeHtml(selectedEmail.sender)}</p>
      <p><strong>Date:</strong> ${escapeHtml(dateStr)}</p>
      <p><strong>Subject:</strong> ${escapeHtml(selectedEmail.subject)}</p>
      <p><strong>To:</strong> ${escapeHtml(selectedEmail.recipient)}</p>
      ${ccHtml}
      <hr>
      ${selectedEmail.body || '<p></p>'}
    `;

    setForwardSubject(`Fwd: ${selectedEmail.subject || ''}`.trim());
    setForwardBody(quotedBody);
    setForwardSelectedAttachments((selectedEmail.attachments || []).map(a => a.id));
    setShowForwardModal(true);
  };

  // Toggle whether an original attachment is carried over
  const toggleForwardAttachment = (id) => {
    setForwardSelectedAttachments(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  // Recipient pill helpers
  const addForwardRecipient = (email) => {
    const cleaned = email.trim();
    if (!cleaned) return;
    setForwardRecipients(prev =>
      prev.includes(cleaned) ? prev : [...prev, cleaned]
    );
  };

  const removeForwardRecipient = (index) => {
    setForwardRecipients(prev => prev.filter((_, i) => i !== index));
  };

  const handleForwardToKeyDown = (e) => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      if (forwardToInput.trim()) {
        addForwardRecipient(forwardToInput);
        setForwardToInput('');
      }
    } else if (e.key === 'Backspace' && !forwardToInput && forwardRecipients.length > 0) {
      setForwardRecipients(prev => prev.slice(0, -1));
    }
  };

  const handleForwardToPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    pasted.split(/[,\n;]/).map(s => s.trim()).filter(Boolean).forEach(addForwardRecipient);
  };

  // Send the forwarded email
  const handleSendForward = async () => {
    const recipients = [
      ...forwardRecipients,
      ...(forwardToInput.trim() ? [forwardToInput.trim()] : [])
    ];

    if (recipients.length === 0) {
      showToast('Please enter a recipient email address', 'error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalid = recipients.find(r => !emailRegex.test(r));
    if (invalid) {
      showToast(`Invalid email format: ${invalid}`, 'error');
      return;
    }

    if (!forwardSubject.trim()) {
      showToast('Please enter a subject', 'error');
      return;
    }
    if (!forwardBody || !forwardBody.trim()) {
      showToast('Please enter a message', 'error');
      return;
    }

    setForwardSending(true);
    try {
      const response = await axios.post(`/inbox/api/${forwardingEmail.id}/forward`, {
        to: recipients.join(', '),
        subject: forwardSubject.trim(),
        body: forwardBody,
        attachmentIds: forwardSelectedAttachments
      });

      if (response.data.status === 'success') {
        showToast(response.data.message, 'success');
        setShowForwardModal(false);
        setForwardingEmail(null);
        setForwardRecipients([]);
        setForwardToInput('');
        fetchMessages();
        fetchCounts();
      } else {
        showToast(response.data.message || 'Failed to forward email', 'error');
      }
    } catch (error) {
      console.error('Error forwarding email:', error);
      showToast(error.response?.data?.message || 'Failed to forward email', 'error');
    } finally {
      setForwardSending(false);
    }
  };

  // Get tab display name
  const getTabName = (tab) => {
    switch(tab) {
      case 'inbox': return 'Inbox';
      case 'unread': return 'Unread';
      case 'archived': return 'Archived';
      case 'trash': return 'Trash';
      case 'sent': return 'Sent';
      default: return 'Inbox';
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Page Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[--asfi-purple] flex items-center">
            <Mail className="w-6 h-6 mr-3" />
            Messages - {getTabName(activeTab)}
          </h1>
          <p className="text-gray-600 mt-1">Your messages and notifications</p>
        </div>
        <button
          onClick={fetchMessages}
          className="p-2 text-gray-500 hover:text-[--asfi-purple] hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Email Container */}
             {/* Tabs */}
          <div className="bg-white border-b border-gray-200 flex">
            <div className="flex">
              <button
                onClick={() => setActiveTab('inbox')}
                className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${
                  activeTab === 'inbox'
                    ? 'border-[--asfi-purple] text-[--asfi-purple]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Mail className="w-4 h-4" />
                Inbox
                {counts.inbox > 0 && (
                  <span className="ml-1 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                    {counts.inbox}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('unread')}
                className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${
                  activeTab === 'unread'
                    ? 'border-[--asfi-purple] text-[--asfi-purple]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <MailOpen className="w-4 h-4" />
                Unread
                {counts.unread > 0 && (
                  <span className="ml-1 bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full text-xs">
                    {counts.unread}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('archived')}
                className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${
                  activeTab === 'archived'
                    ? 'border-[--asfi-purple] text-[--asfi-purple]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Archive className="w-4 h-4" />
                Archived
                {counts.archived > 0 && (
                  <span className="ml-1 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                    {counts.archived}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('sent')}
                className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${
                  activeTab === 'sent'
                    ? 'border-[--asfi-purple] text-[--asfi-purple]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Send className="w-4 h-4" />
                Sent
                {counts.sent > 0 && (
                  <span className="ml-1 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                    {counts.sent}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('trash')}
                className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${
                  activeTab === 'trash'
                    ? 'border-[--asfi-purple] text-[--asfi-purple]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Trash2 className="w-4 h-4" />
                Trash
                {counts.trash > 0 && (
                  <span className="ml-1 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                    {counts.trash}
                  </span>
                )}
              </button>
            </div>
          </div>

      <div className="bg-white rounded-xl shadow-md flex-1 flex overflow-hidden min-h-[600px]">
        {/* Email List - Hidden on mobile when email is open */}
     
        <div className={`${showEmailContent ? 'hidden md:flex' : 'flex'} w-full md:w-2/5 lg:w-1/3 border-r border-gray-200 flex-col`}>
   
          {/* Search Bar */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Bulk Actions */}
          {emails.length > 0 && (
            <div className="p-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-[--asfi-purple] focus:ring-[--asfi-purple] border-gray-300 rounded mr-3"
                />
                <span className="text-xs text-gray-500">
                  {selectedEmails.length} selected
                </span>
              </div>
              
              {selectedEmails.length > 0 && (
                <div className="flex space-x-2">
                  {activeTab !== 'trash' && activeTab !== 'archived' && (
                    <>
                      <button
                        onClick={() => handleBulkAction('archive')}
                        className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded flex items-center gap-1"
                      >
                        <Archive className="w-3 h-3" />
                        Archive
                      </button>
                      <button
                        onClick={() => handleBulkAction('mark_read')}
                        className="px-2 py-1 text-xs text-green-600 hover:bg-green-50 rounded flex items-center gap-1"
                      >
                        <MailOpen className="w-3 h-3" />
                        Mark Read
                      </button>
                      <button
                        onClick={() => handleBulkAction('mark_unread')}
                        className="px-2 py-1 text-xs text-yellow-600 hover:bg-yellow-50 rounded flex items-center gap-1"
                      >
                        <Mail className="w-3 h-3" />
                        Mark Unread
                      </button>
                    </>
                  )}
                  {activeTab === 'trash' ? (
                    <>
                      <button
                        onClick={() => handleBulkAction('restore')}
                        className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded flex items-center gap-1"
                      >
                        <RotateCcw className="w-3 h-3" />
                        Restore
                      </button>
                      <button
                        onClick={() => handleBulkAction('delete')}
                        className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleBulkAction('trash')}
                      className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Trash
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Bulk Actions for Trash (when no selection) */}
          {activeTab === 'trash' && emails.length > 0 && selectedEmails.length === 0 && (
            <div className="p-2 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              <span className="text-xs text-gray-500">{emails.length} emails</span>
              <div className="flex space-x-2">
                <button
                  onClick={handleRestoreAll}
                  className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded flex items-center gap-1"
                >
                  <RotateCcw className="w-3 h-3" />
                  Restore All
                </button>
                <button
                  onClick={handleEmptyTrash}
                  className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" />
                  Empty Trash
                </button>
              </div>
            </div>
          )}

          {/* Email List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[--asfi-purple] mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Loading messages...</p>
              </div>
            ) : networkError ? (
              <div className="p-8 text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                <p className="text-gray-700 font-medium mb-2">Network Error</p>
                <p className="text-sm text-gray-500 mb-4">Please check your connection</p>
                <button
                  onClick={fetchMessages}
                  className="px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors text-sm"
                >
                  Try Again
                </button>
              </div>
            ) : emails.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No messages found</p>
                {activeTab === 'trash' && (
                  <p className="text-sm mt-2">Trash is empty</p>
                )}
              </div>
            ) : (
              emails.map((email) => (
                <div
                  key={email.id}
                  onClick={() => handleEmailClick(email)}
                  className={`email-item p-4 cursor-pointer hover:bg-gray-50 transition-colors relative group ${
                    selectedEmail?.id === email.id ? 'bg-purple-50 border-l-4 border-[--asfi-purple]' : ''
                  } ${email.status === 'unread' && email.direction === 'received' ? 'bg-blue-50' : ''}`}
                >
                  <div className="flex items-start mb-1">
                    <input
                      type="checkbox"
                      checked={selectedEmails.includes(email.id)}
                      onChange={(e) => handleSelectEmail(e, email.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 text-[--asfi-purple] focus:ring-[--asfi-purple] border-gray-300 rounded mr-3 mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <div className={`font-semibold text-sm truncate flex-1 flex items-center gap-1 ${
                          email.status === 'unread' && email.direction === 'received' ? 'text-gray-900' : 'text-gray-700'
                        }`}>
                          {email.starred && (
                            <Star className="w-3 h-3 text-yellow-500 fill-current flex-shrink-0" />
                          )}
                          <span className="truncate" title={email.subject}>
                            {truncateSubject(email.subject, 40)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 whitespace-nowrap ml-2">
                          {formatDate(email.sent_at || email.date_sent)}
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 truncate mb-1">
                        From: {email.sender}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500 truncate">
                          To: {email.recipient}
                        </div>
                        {email.hasAttachments && (
                          <Paperclip className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons - Visible on hover */}
                  <div className="absolute right-2 top-2 hidden group-hover:flex items-center space-x-1 bg-white shadow-md rounded-lg p-1">
                    {activeTab !== 'trash' ? (
                      <>
                        <button
                          onClick={(e) => handleStarEmail(e, email)}
                          className={`p-1 rounded hover:bg-gray-100 ${
                            email.starred ? 'text-yellow-500' : 'text-gray-400'
                          }`}
                          title={email.starred ? 'Unstar' : 'Star'}
                        >
                          <Star className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => handleArchiveEmail(e, email)}
                          className="p-1 text-gray-400 hover:text-blue-500 hover:bg-gray-100 rounded"
                          title="Archive"
                        >
                          <Archive className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteEmail(e, email)}
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={(e) => handleRestoreEmail(e, email)}
                          className="p-1 text-gray-400 hover:text-blue-500 hover:bg-gray-100 rounded"
                          title="Restore"
                        >
                          <RotateCcw className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => handlePermanentDelete(e, email)}
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded"
                          title="Delete Permanently"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>

                  {email.status === 'unread' && email.direction === 'received' && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Email Content */}
        <div className={`${showEmailContent ? 'flex' : 'hidden md:flex'} w-full md:w-3/5 lg:w-2/3 email-content flex-col`}>
          {networkError ? (
            <div className="flex flex-col items-center justify-center h-full p-8">
              <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Network Error</h3>
              <p className="text-gray-600 text-center mb-4">Please check your connection and try again</p>
              <button
                onClick={() => setNetworkError(false)}
                className="px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : selectedEmail ? (
            <div className="flex flex-col h-full">
              {/* Email Header */}
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                <button
                  onClick={handleBackToList}
                  className="md:hidden p-2 rounded-lg hover:bg-gray-200"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h3 className="text-lg font-semibold text-gray-800 flex-1 ml-2 md:ml-0 break-words">
                  {selectedEmail.subject}
                </h3>
                <div className="flex items-center space-x-2">
                  {selectedEmail.status !== 'trashed' ? (
                    <>
                      <button
                        onClick={(e) => handleStarEmail(e, selectedEmail)}
                        className={`p-2 rounded-lg transition-colors ${
                          selectedEmail.starred 
                            ? 'text-yellow-500 hover:bg-yellow-50' 
                            : 'text-gray-500 hover:text-yellow-500 hover:bg-gray-100'
                        }`}
                        title={selectedEmail.starred ? 'Unstar' : 'Star'}
                      >
                        <Star className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleArchiveEmail(e, selectedEmail)}
                        className="p-2 text-gray-500 hover:text-blue-500 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Archive"
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteEmail(e, selectedEmail)}
                        className="p-2 text-gray-500 hover:text-red-500 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={(e) => handleRestoreEmail(e, selectedEmail)}
                        className="p-2 text-gray-500 hover:text-blue-500 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Restore"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handlePermanentDelete(e, selectedEmail)}
                        className="p-2 text-gray-500 hover:text-red-500 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Delete Permanently"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Email Metadata */}
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-[--asfi-purple] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-semibold">
                      {selectedEmail.sender?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 break-words">
                      {selectedEmail.sender === user?.email ? 'You' : selectedEmail.sender}
                    </div>
                    <div className="text-sm text-gray-500 break-words">{selectedEmail.sender}</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center text-gray-600">
                    <User className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                    <span className="break-words">To: {selectedEmail.recipient}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                    {new Date(selectedEmail.sent_at || selectedEmail.date_sent).toLocaleString()}
                  </div>
                </div>

                {/* CC Recipients */}
                {selectedEmail.cc && selectedEmail.cc.length > 0 && (
                  <div className="mt-2 text-sm break-words">
                    <span className="text-gray-500">CC: </span>
                    {selectedEmail.cc.join(', ')}
                  </div>
                )}

                {/* BCC Recipients (only visible to sender) */}
                {selectedEmail.bcc && selectedEmail.bcc.length > 0 && selectedEmail.sender === user?.email && (
                  <div className="mt-2 text-sm break-words">
                    <span className="text-gray-500">BCC: </span>
                    {selectedEmail.bcc.join(', ')}
                  </div>
                )}
              </div>

              {/* Email Body */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* <div 
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: selectedEmail.body || '<p>No content</p>' }}
                /> */}
                <RichTextViewer content={selectedEmail.body} />

                {/* Attachments */}
                {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                      <Paperclip className="w-4 h-4 mr-2" />
                      Attachments ({selectedEmail.attachments.length})
                    </h4>
                    <div className="space-y-2">
                      {selectedEmail.attachments.map((attachment) => (
                        <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center min-w-0">
                            <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3 flex-shrink-0">
                              <FileText className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-700 truncate">{attachment.file_name}</p>
                              {attachment.file_size && (
                                <p className="text-xs text-gray-500">
                                  {(attachment.file_size / 1024).toFixed(1)} KB
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownloadAttachment(selectedEmail.id, attachment.id, attachment.file_name)}
                            className="p-2 text-gray-500 hover:text-[--asfi-purple] hover:bg-white rounded-lg transition-colors flex-shrink-0"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Email Actions */}
              {selectedEmail.status !== 'trashed' && (
                <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
                  {/* <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                    Reply
                  </button> */}
                  <button
                    onClick={handleForwardClick}
                    className="px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors flex items-center gap-2"
                  >
                    <Forward className="w-4 h-4" />
                    Forward
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-8">
              <Mail className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Select a message</h3>
              <p className="text-gray-500 text-center">Choose a message from your inbox to view its content</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && emailToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Move to Trash</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to move this email to trash? You can restore it later from the trash folder.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setEmailToDelete(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Move to Trash
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Forward Email Modal */}
      {showForwardModal && forwardingEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-6 pb-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Forward className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Forward Email</h3>
                  <p className="text-sm text-gray-500">Forward to a different address</p>
                </div>
              </div>
              <button
                onClick={() => setShowForwardModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap items-center gap-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-[--asfi-purple] focus-within:border-transparent text-sm">
                  {forwardRecipients.map((email, index) => (
                    <span
                      key={email}
                      className="inline-flex items-center bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full text-xs font-medium"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => removeForwardRecipient(index)}
                        className="ml-1.5 text-purple-400 hover:text-purple-700 transition-colors"
                        title="Remove recipient"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={forwardToInput}
                    onChange={(e) => setForwardToInput(e.target.value)}
                    onKeyDown={handleForwardToKeyDown}
                    onPaste={handleForwardToPaste}
                    placeholder={forwardRecipients.length === 0 ? "Enter recipient email" : ""}
                    className="flex-1 min-w-[150px] outline-none border-0 focus:ring-0 p-0"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Press comma or Enter to add multiple recipients.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={forwardSubject}
                  onChange={(e) => setForwardSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Add your message above the forwarded email content.
                </p>
                <RichTextEditor
                  value={forwardBody}
                  onChange={setForwardBody}
                  onImageExtracted={uploadEditorImage}
                  placeholder="Type your message here..."
                  minHeight="250px"
                  maxHeight="350px"
                />
              </div>

              {/* Original Attachments */}
              {forwardingEmail.attachments && forwardingEmail.attachments.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Paperclip className="w-4 h-4 mr-2 text-gray-400" />
                    Attachments to Forward ({forwardingEmail.attachments.length})
                  </h4>
                  <div className="space-y-2">
                    {forwardingEmail.attachments.map((attachment) => (
                      <label
                        key={attachment.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer"
                      >
                        <div className="flex items-center min-w-0">
                          <input
                            type="checkbox"
                            checked={forwardSelectedAttachments.includes(attachment.id)}
                            onChange={() => toggleForwardAttachment(attachment.id)}
                            className="h-4 w-4 text-[--asfi-purple] focus:ring-[--asfi-purple] border-gray-300 rounded mr-3"
                          />
                          <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3 flex-shrink-0">
                            <FileText className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate">{attachment.file_name}</p>
                            {attachment.file_size && (
                              <p className="text-xs text-gray-500">
                                {(attachment.file_size / 1024).toFixed(1)} KB
                              </p>
                            )}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 pt-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowForwardModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendForward}
                disabled={forwardSending}
                className="px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {forwardSending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Forward
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inbox;
